import { useState, useEffect, useRef, Fragment } from "react"
import { formatDistanceToNow } from "date-fns"
import { MoreHorizontal } from "lucide-react"
import { Avatar, AvatarFallback } from "@/modules/shared/ui/components/avatar"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/modules/shared/ui/components/dropdown-menu"
import { ReactionPicker } from "@/modules/recruitment/applications/ui/components/comments/ReactionPicker"
import { CommentComposer } from "@/modules/recruitment/applications/ui/components/comments/CommentComposer"
import { ApplicationComment, ApplicationCommentReactionEmoji } from "@/modules/recruitment/applications/dtos/ApplicationComment"
import {
  useUpdateApplicationComment,
  useDeleteApplicationComment,
  useToggleApplicationCommentReaction,
} from "@/modules/recruitment/applications/hooks/useApplicationComments"

const MENTION_PATTERN = /(@[A-Z][a-zA-Z]*(?:\s[A-Z][a-zA-Z]*)?)/g
const HIGHLIGHT_FADE_MS = 3000

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

function renderTextWithMentions(text: string) {
  return text.split(MENTION_PATTERN).map((part, i) =>
    MENTION_PATTERN.test(part) ? (
      <span key={i} className="text-primary font-medium">
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  )
}

interface CommentItemProps {
  comment: ApplicationComment
  organizationId: string
  applicationId: string
  isReply?: boolean
  highlightCommentId?: string
  readOnly?: boolean
}

export function CommentItem({ comment, organizationId, applicationId, isReply = false, highlightCommentId, readOnly = false }: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(comment.text)
  const [isReplying, setIsReplying] = useState(false)

  const updateComment = useUpdateApplicationComment(organizationId, applicationId)
  const deleteComment = useDeleteApplicationComment(organizationId, applicationId)
  const toggleReaction = useToggleApplicationCommentReaction(organizationId, applicationId)

  const isTarget = comment.id === highlightCommentId
  const [showHighlight, setShowHighlight] = useState(isTarget)
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isTarget) return
    itemRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    setShowHighlight(true)
    const timer = setTimeout(() => setShowHighlight(false), HIGHLIGHT_FADE_MS)
    return () => clearTimeout(timer)
  }, [isTarget])

  const highlightClassName = showHighlight ? "bg-primary/5 ring-1 ring-primary/40" : ""

  const handleSaveEdit = () => {
    const trimmed = editText.trim()
    if (!trimmed) return
    updateComment.mutate(
      { commentId: comment.id, text: trimmed },
      { onSuccess: () => setIsEditing(false) },
    )
  }

  const handleToggleReaction = (emoji: ApplicationCommentReactionEmoji) => {
    toggleReaction.mutate({ commentId: comment.id, emoji })
  }

  if (comment.isDeleted) {
    return (
      <div ref={itemRef} className={`flex gap-3 rounded-lg transition-colors duration-700 ${highlightClassName}`}>
        <Avatar className={isReply ? "size-6.5" : "size-8.5"}>
          <AvatarFallback className="text-xs text-muted-foreground">—</AvatarFallback>
        </Avatar>
        <p className="text-sm italic text-muted-foreground pt-1.5">This comment was deleted.</p>
      </div>
    )
  }

  return (
    <div ref={itemRef} className={`flex gap-3 rounded-lg transition-colors duration-700 ${highlightClassName}`}>
      <Avatar className={isReply ? "size-6.5" : "size-8.5"}>
        <AvatarFallback className="text-xs font-medium">{getInitials(comment.authorName)}</AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-sm">{comment.authorName}</span>
          <span className="text-muted-foreground text-xs">
            {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
          </span>
          {comment.isEdited && <span className="text-muted-foreground text-xs">(edited)</span>}

          {!readOnly && (comment.canEdit || comment.canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="ml-auto text-muted-foreground"
                  aria-label="Comment actions"
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {comment.canEdit && (
                  <DropdownMenuItem
                    onClick={() => {
                      setEditText(comment.text)
                      setIsEditing(true)
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                )}
                {comment.canDelete && (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => deleteComment.mutate({ commentId: comment.id })}
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {isEditing ? (
          <div className="mt-1.5 space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              maxLength={1000}
              className="min-h-14 text-sm"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={!editText.trim() || updateComment.isPending}
                className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-md disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="bg-muted text-muted-foreground text-xs font-medium px-3 py-1.5 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm mt-1 whitespace-pre-wrap break-words">{renderTextWithMentions(comment.text)}</p>
        )}

        {!isEditing && (
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <ReactionPicker
              reactions={comment.reactions}
              onToggle={handleToggleReaction}
              disabled={toggleReaction.isPending || readOnly}
            />
            {!isReply && !readOnly && (
              <button
                type="button"
                onClick={() => setIsReplying((v) => !v)}
                className="text-xs text-muted-foreground font-medium"
              >
                Reply
              </button>
            )}
          </div>
        )}

        {isReplying && !isReply && (
          <div className="mt-3">
            <CommentComposer
              organizationId={organizationId}
              applicationId={applicationId}
              parentCommentId={comment.id}
              placeholder={`Reply to ${comment.authorName}...`}
              onPosted={() => setIsReplying(false)}
            />
          </div>
        )}

        {!isReply && comment.replies.length > 0 && (
          <div className="mt-4 space-y-4 pl-4 border-l border-border">
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                organizationId={organizationId}
                applicationId={applicationId}
                isReply
                highlightCommentId={highlightCommentId}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
